<template>
    <sub-section title="What will my payments and investments look like based on my current plan?" :showHelp="false">
        <p>{{ taxFreeDrawdownText }}</p>
        <drawdown-chart ref="drawdownChartA" :title="drawdownChartTitle" :chartData="points"></drawdown-chart>

        <p>
            Choose the number of years between 5 and 10 you would like to more closely match below. You can then see the fund splits based 
            on your current plan. A higher percentage in the Matching Income Fund provides less risk. However, it is a trade 
            off, as more in the Responsible Growth Fund may provide higher returns (with more risk) over the long term.
        </p>

        <div class="col-md-7 text-start d-flex flex-wrap align-items-center">
            <span class="me-2 text-nowrap">
                Matched years:
                <span class="fw-bold" style="display: inline-block; width: 2ch; text-align: right;">
                    {{ matchedPaymentsModel }}
                </span>
            </span>
            <div class="d-flex flex-grow-1 align-items-center mt-2 mt-md-0">
                <span class="ms-4 me-3">5</span>
                <input
                    type="range"
                    class="form-range my-3 flex-grow-1"
                    v-model="matchedPaymentsModel"
                    min="5"
                    max="10"
                    step="1"
                />
                <span class="ms-3">10</span>
            </div>
        </div>

        <div class="mb-2">
            <investment-allocation-chart
                :title="allocationChartTitle"
                :data="allocationChartData"
                @show-calcs="showSplitsCalcs">
            </investment-allocation-chart>
        </div>

        <div v-if="showYearsBeforeRetirement" class="col-md-7 text-start d-flex flex-wrap align-items-center mb-2">
            <span class="me-2 text-nowrap">
                Years until retirement:
                <duration-in-years :duration="selectedPointInTime"></duration-in-years>
            </span>
            <div class="d-flex flex-grow-1 align-items-center mt-2 mt-md-0">
                 <span class="ms-4 me-3">Now</span>
                 <input
                    type="range"
                    class="form-range my-3 flex-grow-1"
                    v-model="yearsBeforeRetirementModel"
                    :min="yearsBeforeRetirementMin"
                    max="0"
                    step="1"
                />
                <span class="ms-3">0</span>
            </div>
        </div>

        <div v-if="false" class="form-check form-switch d-flex mt-2 ps-0">
            <label class="form-check-label me-3 ps-0" for="toggleShowFundAllocInDrawdown">
                Show Matching Income/Responsible Growth fund split in Pension Drawdown chart:
            </label>
            <input id="toggleShowFundAllocInDrawdown" type="checkbox" class="form-check-input ms-0" role="switch" v-model="showFundAllocInDrawdownChart" />
        </div>

        <div v-if="showTaxFreeLumpSumQuestion" class="mt-2 mb-3">
            <options-question name="taxFreeLumpSumFund" :question="lumpSumFundAllocQuestionText" :options="lumpSumFundAllocOptions"
                v-model:selected="lumpSumAllocOptionModel">
                <help title="Tax free lump sum holding fund" :text="HelpText.taxFreeLumpSumHoldingFund()" />
            </options-question>
        </div>
    </sub-section>

    <modal title="Fund splits" ref="splitsModal" idKey="splitsModalKey">
        <fund-split-explanation :fund-split-info="fundSplitInfo"></fund-split-explanation>
    </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import { Retiree } from "../../ts/retiree";
import { RetirementPlanResponse } from "@/guiide/ts/response/retirementPlanResponse";
import { useStore } from "vuex";
import { getPensionPotDrawdownAmounts, getYearsFromRetirementDate } from "@/guiide/ts/utilities";
import fundInfo from "@/guiide/ts/funds";
import Fund from "@/guiide/ts/fund";
import Constants from "@/guiide/ts/constants";
import PatternFillType from "@/guiide/ts/charts/patternFill";
import patternFills from "@/guiide/ts/charts/patternFills";
import ChartInfo from "@/guiide/ts/chartInfo";
import { PointOptionsObject } from "highcharts";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import SubSection from "../common/SubSection.vue";
import DrawdownChart from "../charts/DrawdownChart.vue";
import OptionsQuestion from "@/guiide/components/common/OptionsQuestion.vue";
import InvestmentAllocationChart from "../charts/InvestmentAllocationChart.vue";
import Modal from "../common/Modal.vue";
import FundSplitExplanation from "./FundSplitExplanation.vue";
import DurationInYears from "../common/DurationInYears.vue";
import FundSplitInfo from "@/guiide/ts/secure/fundSplitInfo";
import FundAllocation from "@/guiide/ts/fundAllocation";
import { getDuration } from "@/guiide/ts/temporal/durationHelper";
import FundAllocationCalculator from "@/guiide/ts/secure/fundAllocationCalculator";
import FundAllocationCalculation from "@/guiide/ts/secure/fundAllocationCalculation";

const props = defineProps({
    retiree: {
        type: Object as () => Retiree | null,
        default: () => null,
    },
    pensionPotDrawdownAmounts: {
        type: Array<number>,
        default: new Array<number>()
    }
});

defineExpose({ getAllocationInformation });

const store = useStore();

const matchedPaymentsModel = ref(5);
const lumpSumAllocOptionModel = ref(fundInfo.allFunds.filter(f => f.canHoldLumpSum)[0].name);
const yearsBeforeRetirementMin = ref(-30);
const yearsBeforeRetirementModel = ref(0);
const drawdownChartA = ref(null);
const showFundAllocInDrawdownChart = ref(false);
const allocationData = ref([
    getDefaultDataPointForFund(fundInfo.trusteeBankAccount),
    getDefaultDataPointForFund(fundInfo.moneyFund),
    getDefaultDataPointForFund(fundInfo.matchingIncomeFund),
    getDefaultDataPointForFund(fundInfo.responsibleGrowthFund)
]);
const splitsModal = ref(null);
const fundSplitInfo = ref<FundSplitInfo>(new FundSplitInfo(100000, 5, 4, false, 3, new Array<PointOptionsObject>(), new Array<FundAllocation>()));
const showYearsBeforeRetirement = ref(true);

const matchedYearColor = Constants.Color.lightEmerald;
const unmatchedYearColor = Constants.Color.brunswickGreen;

onMounted(() => {
    if (store.state.retiree != null && store.state.retiree.yearsToRetirement != null) {
        initialiseYearsBeforeRetirement(store.state.retiree.yearsToRetirement);
    }
});

watch(
    () => store.state.retiree,
    async (newValue: Retiree) => {
        if (newValue == null || newValue.yearsToRetirement == null) {
            return;
        }
        await initialiseYearsBeforeRetirement(store.state.retiree.yearsToRetirement);
    }
);

async function initialiseYearsBeforeRetirement(yearsToRetirement: number) {
    let years = -1 * yearsToRetirement;
    let labelValue = years;

    if (hasPartialRetirementYear.value) {
        years--;
        const durationFromRet = Math.abs(getYearsFromRetirementDate());
        const duration = getDuration(durationFromRet);
        labelValue = getYearsFromRetirementDate();
    }

    yearsBeforeRetirementMin.value = years;
    yearsBeforeRetirementModel.value = years;

    showYearsBeforeRetirement.value = Math.abs(yearsBeforeRetirementMin.value) > 0;
}

watch(
    () => yearsBeforeRetirementMin.value,
    (newValue) => {
        if (yearsBeforeRetirementModel.value < newValue) {
            yearsBeforeRetirementModel.value = newValue;
        }
    }
);

const lumpSumFundAllocQuestionText = "You expect to take 25% of your pots as a tax free cash lump sum at retirement. Choose between putting that 25% in the Money "
    + "Fund now, or leaving it in the Growth Fund now and moving it nearer retirement. The Money Fund is a close match to the lump sum wanted, but if retirement is "
    + "still some time off you may wish to stay invested in the Growth Fund for a while longer.";
const lumpSumFundAllocOptions = fundInfo.allFunds.filter(f => f.canHoldLumpSum).map(f => f.name);
const showTaxFreeLumpSumQuestion = computed<boolean>(() => store.state.retiree?.requirements.takingTaxFreeAsLumpSum ?? false);

const taxFreeDrawdownText = computed<string>(() => {
    if (store.state.retiree == null) {
        return "";
    }

    const isTakingLumpSum = store.state.retiree.requirements.takingTaxFreeAsLumpSum;

    return isTakingLumpSum
        ? "You will take 25% of your pot as a tax free cash lump sum at retirement. Then take the pension pot payments shown in the chart below in each year."
        : "You will take the pension pot payments shown in the chart below in each year. 25% of each payment will be tax free.";
});

const pensionPotDrawdownAmounts = computed<Array<number>>(() => {
    return getPensionPotDrawdownAmounts(store.state.plan);
})

function getDataPoint(y: number, colorName: string, patternFillType: PatternFillType, strokeWidth: number, name: string = ''): PointOptionsObject {
    let color: any = getPatternFillObject(patternFillType, colorName, strokeWidth);
    return { y, color, name };
}

function getPatternFillObject(patternFillType: PatternFillType, colorName: string, strokeWidth: number) {
    if (patternFillType == PatternFillType.None) {
        return colorName;
    }

    return {
        pattern: {
            path: {
                d: patternFills.getByType(patternFillType),
                strokeWidth: strokeWidth
            },
            width: 10,
            height: 10,
            color: colorName,
            opacity: 1
        }
    }; 
}

const drawdownChartTitle = computed<string>(() => {
    if (!showFundAllocInDrawdownChart.value || store.state.retiree == null) {
        return "Pension drawdown";
    }
    
    const pointInTimeText = getChartPointInTimeTitleText();
    return `Pension drawdown (with fund splits ${pointInTimeText})`;
});

const allocationChartTitle = computed<string>(() => {
    if (store.state.retiree == null) {
        return "Fund splits";
    }

    const pointInTimeText = getChartPointInTimeTitleText();
    return `Fund splits ${pointInTimeText}`;
});

const points = ref<Array<PointOptionsObject>>(new Array<PointOptionsObject>());

watch(
    () => pensionPotDrawdownAmounts.value,
    (newPensionPotDrawdownAmounts) => {
        initialiseDataPoints();
    }
)

function initialiseDataPoints() {
    points.value.length = 0;

    pensionPotDrawdownAmounts.value.map((a, i) => {
        const isMatchedYear = i < matchedPaymentsModel.value;
        const isHeldInMatchingIncome = i < numberOfYearsHeldInMatchingIncome.value;

        const colorName = isMatchedYear ? matchedYearColor : unmatchedYearColor;

        const allocatedFund = isHeldInMatchingIncome ? fundInfo.matchingIncomeFund : fundInfo.responsibleGrowthFund;
        const patternFillType = showFundAllocInDrawdownChart.value
            ? allocatedFund.defaultChartInfo.patternFillType
            : PatternFillType.None;
        const strokeWidth = allocatedFund.defaultChartInfo.strokeWidth;

        const pointOptionsObject = getDataPoint(a, colorName, patternFillType, strokeWidth);

        points.value.push(pointOptionsObject);
    });

    return points;
};

function updateDrawdownDataPoints() {
    let yearIndex = 0;
    for (const pointOptionsObject of points.value) {
        const columnProps = getDrawdownColumnProperties(yearIndex);
        const updateSourceDataPoint = getDataPoint(pointOptionsObject.y, columnProps.colorName, columnProps.patternFillType, columnProps.strokeWidth);
        pointOptionsObject.color = updateSourceDataPoint.color;
        yearIndex++;
    }
}

function getDrawdownColumnProperties(yearIndex: number): { colorName: string, patternFillType: PatternFillType, strokeWidth: number } {
    const isMatchedYear = yearIndex < matchedPaymentsModel.value;
    const isHeldInMatchingIncome = yearIndex < numberOfYearsHeldInMatchingIncome.value;

    const colorName = isMatchedYear ? matchedYearColor : unmatchedYearColor;

    const allocatedFund = isHeldInMatchingIncome ? fundInfo.matchingIncomeFund : fundInfo.responsibleGrowthFund;
    const patternFillType = showFundAllocInDrawdownChart.value
        ? allocatedFund.defaultChartInfo.patternFillType
        : PatternFillType.None;

    // TODO
    const strokeWidth = isHeldInMatchingIncome ? 3 : 4;

    return { colorName, patternFillType, strokeWidth };
}

const allocationChartData = computed(() => {
    return allocationData.value;
});

const numberOfYearsHeldInMatchingIncome = computed<number>(() => Math.max(0, matchedPaymentsModel.value - absSelectedYearsToRetirement.value));

const selectedTaxFreeLumpSumHoldingFund = computed<Fund>(() => fundInfo.allFunds.filter(f => f.name == lumpSumAllocOptionModel.value)[0]);

const retirementPlan = computed<RetirementPlanResponse>(() => store.state.plan);

const absSelectedYearsToRetirement = computed<number>(() => Math.abs(yearsBeforeRetirementModel.value));

const selectedPointInTime = computed<number>(() => {
    if (!hasPartialRetirementYear.value) {
        return Math.abs(yearsBeforeRetirementModel.value);
    }

    if (selectedPointInTimeIsNow.value) {
        return Math.abs(getYearsFromRetirementDate() ?? 0);
    }

    return Math.abs(yearsBeforeRetirementModel.value);
});

const selectedPointInTimeIsNow = computed<boolean>(() => yearsBeforeRetirementModel.value == yearsBeforeRetirementMin.value);

const hasPartialRetirementYear = computed<boolean>(() => {
    if (store.state.retiree == null || store.state.retiree.alreadyDrawingPension) {
        return false;
    }

    const pensionPots = store.state.retiree.pensionPots;

    if (pensionPots.estPreRetirementValuations.length == 0) {
        return false;
    }

    const mostRecentYear = pensionPots.estPreRetirementValuations[pensionPots.estPreRetirementValuations.length - 1];

    return mostRecentYear.amount != pensionPots.presentValue;
});

watch(
    () => [ store.state.retiree, matchedPaymentsModel.value, retirementPlan.value, lumpSumAllocOptionModel.value, absSelectedYearsToRetirement.value, showFundAllocInDrawdownChart.value ],
    () => {
        recalculateAllocations();
        updateDrawdownDataPoints();
        updateFundSplitInfo();
    });

function getPensionPotsTotalValueAtSelectedTime(): number {
    if (selectedPointInTimeIsNow.value && hasPartialRetirementYear.value) {
        return store.state.retiree.pensionPots.presentValue;
    }

    const preRetirementValuationIndex = Math.abs(yearsBeforeRetirementModel.value);
    return store.state.retiree.pensionPots.estPreRetirementValuations[preRetirementValuationIndex].amount;
}

function recalculateAllocations() {
    if (store.state.retiree == null || retirementPlan.value == null) {
        return;
    }

    const pensionPotsTotalValueAtSelectedTime = getPensionPotsTotalValueAtSelectedTime();

    const allocCalcResult = FundAllocationCalculator.calculate(
        pensionPotsTotalValueAtSelectedTime,
        props.pensionPotDrawdownAmounts,
        store.state.retiree.alreadyDrawingPension,
        store.state.retiree.requirements.takingTaxFreeAsLumpSum,
        selectedTaxFreeLumpSumHoldingFund.value,
        numberOfYearsHeldInMatchingIncome.value);

    const usePatternFill = showFundAllocInDrawdownChart.value;

    updateAllocationDataPoint(0, allocCalcResult.trusteeBankAccount.percentage, false, fundInfo.trusteeBankAccount.defaultChartInfo);
    updateAllocationDataPoint(1, allocCalcResult.moneyFund.percentage, usePatternFill, fundInfo.moneyFund.defaultChartInfo);
    updateAllocationDataPoint(2, allocCalcResult.matchingIncomeFund.percentage, usePatternFill, fundInfo.matchingIncomeFund.defaultChartInfo);
    updateAllocationDataPoint(3, allocCalcResult.responsibleGrowthFund.percentage, usePatternFill, fundInfo.responsibleGrowthFund.defaultChartInfo);

    fundSplitInfo.value.valueOfPensionPots = pensionPotsTotalValueAtSelectedTime;
    fundSplitInfo.value.taxFreeLumpSumAmount = allocCalcResult.lumpSumAmount;
    fundSplitInfo.value.allocations = allocCalcResult.allocations;
}

function updateAllocationDataPoint(dataPointIndex: number, percentage: number, usePatternFill: boolean, chartInfo: ChartInfo) {
    const dataPoint: PointOptionsObject = allocationData.value[dataPointIndex];

    if (usePatternFill && chartInfo.patternFillType != PatternFillType.None) {
        allocationData.value[dataPointIndex].color = getPatternFillObject(chartInfo.patternFillType, chartInfo.color, chartInfo.strokeWidth);
    } else {
        allocationData.value[dataPointIndex].color = chartInfo.color;
    }

    dataPoint.y = percentage;
}

function getDefaultDataPointForFund(fund: Fund, value: number = 100, usePatternFile: boolean = false) {
    const patternFillType = usePatternFile ? fund.defaultChartInfo.patternFillType : PatternFillType.None;

    return getDataPoint(
        value,
        fund.defaultChartInfo.color,
        patternFillType,
        fund.defaultChartInfo.strokeWidth,
        fund.name
    );
}

function getChartPointInTimeTitleText(): string {
    const retirementDate = new Date(store.state.retiree.retirementDate);
    const yearExamined = retirementDate.getFullYear() + Number(yearsBeforeRetirementModel.value);
    let pointInTimeText = `in ${yearExamined}`;

    if (yearsBeforeRetirementModel.value == 0) {
        pointInTimeText = `at retirement in ${yearExamined}`;
    } else if (yearsBeforeRetirementModel.value == yearsBeforeRetirementMin.value) {
        pointInTimeText = "if transferred today";
    }

    return pointInTimeText;
}

function getAllocationInformation(): { matchedYears: number, taxFreeLumpSumFundId: number|null, fundAllocCalcAtPresent: FundAllocationCalculation,
    fundAllocCalcAtRetirement: FundAllocationCalculation } {
    let taxFreeLumpSumFundId: number|null = null;

    if (showTaxFreeLumpSumQuestion.value) {
        taxFreeLumpSumFundId = selectedTaxFreeLumpSumHoldingFund.value.id;
    }

    const numberOfYearsMatchedAtPresent = matchedPaymentsModel.value - Math.abs(yearsBeforeRetirementMin.value);
    console.log("calc numberOfYearsMatchedAtPresent", numberOfYearsMatchedAtPresent);

    const allocCalcResultAtPresent = FundAllocationCalculator.calculate(
        store.state.retiree.pensionPots.presentValue,
        props.pensionPotDrawdownAmounts,
        store.state.retiree.alreadyDrawingPension,
        store.state.retiree.requirements.takingTaxFreeAsLumpSum,
        selectedTaxFreeLumpSumHoldingFund.value,
        numberOfYearsMatchedAtPresent);

    const allocCalcResultAtRetirement = FundAllocationCalculator.calculate(
        store.state.retiree.pensionPots.valueAtRetirement,
        props.pensionPotDrawdownAmounts,
        store.state.retiree.alreadyDrawingPension,
        store.state.retiree.requirements.takingTaxFreeAsLumpSum,
        selectedTaxFreeLumpSumHoldingFund.value,
        matchedPaymentsModel.value);

    return { 
        matchedYears: matchedPaymentsModel.value, 
        taxFreeLumpSumFundId, 
        fundAllocCalcAtPresent: allocCalcResultAtPresent,
        fundAllocCalcAtRetirement: allocCalcResultAtRetirement
    };
}

function showSplitsCalcs() {
    splitsModal.value.showModal();
}

function updateFundSplitInfo() {
    fundSplitInfo.value.drawdowns = points.value;
    fundSplitInfo.value.matchedYears = matchedPaymentsModel.value;
    fundSplitInfo.value.yearsToRetirement = Math.abs(yearsBeforeRetirementModel.value);
    fundSplitInfo.value.isPresentTime = yearsBeforeRetirementModel.value == yearsBeforeRetirementMin.value;
    const matchedYearsInWindow = Math.max(0, matchedPaymentsModel.value - Math.abs(yearsBeforeRetirementModel.value));
    fundSplitInfo.value.matchedYearsInWindow = matchedYearsInWindow;
    fundSplitInfo.value.isTakingTaxFreeLumpSum = showTaxFreeLumpSumQuestion.value;
    fundSplitInfo.value.taxFreeLumpSumAllocFund = selectedTaxFreeLumpSumHoldingFund.value;
}
</script>

<style scoped>

</style>
